import { AnnouncementBase } from '.';
import { Log } from '../../../AuxillaryService/Log';
import { VideoHandlerInterface, DetachedDomElementError } from '../../../MediaController/Video';
import { AnnouncementWidget } from '../AnnouncementWidget';
import { DEFAULT_BACKGROUND_COLOR, VideoBaseAnnouncementConfiguration } from '../types';
import type { PlayTimes } from '../PlayTimes';
import { videoTemplate } from '../Templates';

export abstract class AnnouncementVideoBase<
    T extends VideoBaseAnnouncementConfiguration
> extends AnnouncementBase {
    /** The HTML Element ID of the Player's Container Element */
    protected abstract _playerElementId: string;
    protected abstract _player: VideoHandlerInterface;
    protected _className: string;

    protected constructor(configuration: T, widget: AnnouncementWidget, playTimes: PlayTimes) {
        super(configuration, widget, playTimes);
        this._className = new.target.name;
    }

    protected renderTemplate(): string {
        return videoTemplate({
            containerElementId: this.containerElementId,
            announcement: {
                name: this.configuration.name,
                background_color: this.configuration.background_color ?? DEFAULT_BACKGROUND_COLOR,
            },
            videoElementId: this._playerElementId,
        });
    }

    protected async _updatePlayerElement(): Promise<void> {
        if (this.widget.wrapperElement === null) {
            throw new ReferenceError('Wrapper Element not defined.');
        }
        const element: HTMLElement | null = this.widget.wrapperElement.querySelector(
            `#${this._playerElementId}`
        );
        if (element === null) {
            throw new TypeError('Player Container Element not found.');
        }
        await this._player.setElement(element);
    }

    /**
     * announcement initialize method
     *
     * sets up video announcement for playing
     */
    public async initialize(): Promise<void> {
        Log.Debug(`${this._className}#initialize()`, null, __fileline);
        await this._updatePlayerElement();
    }

    /**
     * announcement run page
     *
     * starts playing video at video start time
     */
    public async runPage(): Promise<void> {
        Log.Debug(`${this._className}#runPage()`, null, __fileline);
        try {
            await this._player.play();
        } catch (error) {
            if (error instanceof ReferenceError || error instanceof DetachedDomElementError) {
                // This is caused by layout switching. Honestly this shouldn't
                // be needed but we're working around the Announcement Tool
                // here.
                Log.Warning(`Re-create player because of error, '${error}'`, null, __fileline);
                await this._updatePlayerElement();
                await this._player.play();
            } else {
                throw error;
            }
        }
    }

    /**
     * announcement deallocate
     *
     * destroys video
     */
    public async deallocate(): Promise<void> {
        await this._player.stop();
    }

    /**
     * announcement stop page
     *
     * stops video
     */
    public async stopPage(): Promise<void> {
        Log.Debug(`${this._className}#stopPage()`, null, __fileline);
        await this.deallocate();
    }
}
